/**
 * PDF Field Mapping Utility
 *
 * Generic loader that reads JSON configuration files and resolves field values
 * from contact data using the configured business logic rules.
 */

// Contact-like interface for PDF filling
export interface ContactLike {
  name?: string;
  email?: string;
  phone?: string | null;
  title?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  company_name?: string | null;
  notes?: string | null;
  website?: string | null;
  industry?: string | null;

  // ---------- Client Checklist ----------
  telecom_contact_name?: string | null;
  telecom_contact_email?: string | null;
  telecom_contact_phone?: string | null;

  // ---------- Waste ----------
  waste_contact_name?: string | null;
  waste_contact_email?: string | null;
  waste_contact_phone?: string | null;

  // ---------- Electronic Logging Devices ----------
  eld_contact_name?: string | null;
  eld_contact_email?: string | null;
  eld_contact_phone?: string | null;

  // ---------- eSignature ----------
  esignature_contact_name?: string | null;
  esignature_contact_email?: string | null;
  esignature_contact_phone?: string | null;

  // ---------- Fuel ----------
  fuel_contact_name?: string | null;
  fuel_contact_email?: string | null;
  fuel_contact_phone?: string | null;

  // ---------- Facility Supplies ----------
  facility_supplies_contact_name?: string | null;
  facility_supplies_contact_email?: string | null;
  facility_supplies_contact_phone?: string | null;

  // ---------- Less Than Truckload ----------
  ltl_contact_name?: string | null;
  ltl_contact_email?: string | null;
  ltl_contact_phone?: string | null;

  // ---------- SaaS ----------
  saas_contact_name?: string | null;
  saas_contact_email?: string | null;
  saas_contact_phone?: string | null;

  // ---------- Uniforms and Linens ----------
  uniforms_contact_name?: string | null;
  uniforms_contact_email?: string | null;
  uniforms_contact_phone?: string | null;

  // ---------- UCaaS ----------
  ucaas_contact_name?: string | null;
  ucaas_contact_email?: string | null;
  ucaas_contact_phone?: string | null;

  // ---------- Compressed Gases ----------
  compressed_gases_contact_name?: string | null;
  compressed_gases_contact_email?: string | null;
  compressed_gases_contact_phone?: string | null;

  // ---------- Decision Maker ----------
  decision_maker_name?: string | null;
  decision_maker_position?: string | null;
  decision_maker_email?: string | null;
  decision_maker_phone?: string | null;

  // ---------- Invoices / Online Access ----------
  invoices_contact_name?: string | null;
  invoices_contact_email?: string | null;
  invoices_contact_phone?: string | null;
}

import type { TemplateFieldConfig } from "../export/types";

export type { TemplateFieldConfig };

export interface TemplateConfig {
  templateId: string;
  templateFile: string;
  displayName: string;
  fields: Record<string, TemplateFieldConfig>;
}

/**
 * Load template configuration from JSON file
 */
export async function loadTemplateConfig(templateId: string): Promise<TemplateConfig> {
  try {
    const config = await import(`@/lib/config/pdf-templates/${templateId}.json`);
    return config.default || config;
  } catch (error) {
    throw new Error(`Failed to load template config for ${templateId}: ${error}`);
  }
}

/**
 * Get contact field value by path
 */
function getContactFieldValue(contact: ContactLike, fieldPath: string): string | undefined {
  const fields: Record<string, string | null | undefined> = {
    name: contact.name,
    email: contact.email,
    phone: contact.phone,
    title: contact.title,
    address: contact.address,
    city: contact.city,
    state: contact.state,
    zip_code: contact.zip_code,
    company_name: contact.company_name,
    notes: contact.notes,
    website: contact.website,
    industry: contact.industry,
  };

  const value = fields[fieldPath];
  return value != null ? String(value) : undefined;
}

/**
 * Resolve field value using JSON config rules (source → fallback → default)
 */
export function resolveFieldValue(contact: ContactLike, fieldConfig: TemplateFieldConfig): string {
  // Try source field
  let value = getContactFieldValue(contact, fieldConfig.source);

  // Try fallback if source is empty
  if ((!value || value.trim() === "") && fieldConfig.fallback) {
    value = getContactFieldValue(contact, fieldConfig.fallback);
  }

  // Use default if both are empty
  if ((!value || value.trim() === "") && fieldConfig.default) {
    value = fieldConfig.default;
  }

  // Return empty string if still no value
  return value?.toString() || "";
}

/**
 * Map contact data to PDF fields using JSON configuration
 */
export async function mapContactToPdfFields(
  contact: ContactLike,
  templateId: string
): Promise<Record<string, string>> {
  const config = await loadTemplateConfig(templateId);
  const mappedFields: Record<string, string> = {};

  for (const [pdfFieldName, fieldConfig] of Object.entries(config.fields)) {
    const value = resolveFieldValue(contact, fieldConfig);

    // For select fields, try to match value to options
    if (fieldConfig.type === "select" && fieldConfig.options && value) {
      // Try exact match first (case-insensitive)
      const matchedOption = fieldConfig.options.find(
        (opt) => opt.toLowerCase() === value.toLowerCase()
      );

      if (matchedOption) {
        mappedFields[pdfFieldName] = matchedOption;
      } else {
        // Try partial match (e.g., "California" matches "CA")
        const partialMatch = fieldConfig.options.find(
          (opt) =>
            value.toLowerCase().includes(opt.toLowerCase()) ||
            opt.toLowerCase().includes(value.toLowerCase())
        );

        mappedFields[pdfFieldName] = partialMatch || fieldConfig.options[0] || "";
      }
    } else {
      mappedFields[pdfFieldName] = value;
    }
  }

  return mappedFields;
}

/**
 * Get all available template IDs
 */
export async function getAvailableTemplates(): Promise<string[]> {
  // This could be enhanced to dynamically discover templates
  return ["client-fact-finding-checklist", "telecom-fact-finding-checklist"];
}
